/**
 * Products page — commercial presentation layer (FR/EN).
 * Catalog / portfolio-policy remain product truth.
 * Do not invent availability, trials, clients, or HIDDEN modules.
 */
import type { PepLocale } from "../i18n/types";

export type ProductsTier = "primary" | "limited";

export type ProductsSlug =
  | "edu"
  | "rh"
  | "mineportal"
  | "inventory"
  | "erp"
  | "billing"
  | "hospitol"
  | "security";

export interface ProductsEditorial {
  slug: ProductsSlug;
  tier: ProductsTier;
  order: number;
  commercialNameFr: string;
  commercialNameEn: string;
  imageSrc: string;
  imageAltFr: string;
  imageAltEn: string;
  promiseFr: string;
  promiseEn: string;
  capacitiesFr: readonly string[];
  capacitiesEn: readonly string[];
  tone: "edu" | "rh" | "mine" | "inventory" | "erp" | "billing" | "hospitol" | "security";
}

export interface ProductsHeroPanel {
  id: string;
  imageSrc: string;
  labelFr: string;
  labelEn: string;
  altFr: string;
  altEn: string;
}

/** Commercial names — never expose raw slugs as titles. */
export const PRODUCTS_COMMERCIAL_NAMES: Record<
  ProductsSlug,
  { fr: string; en: string }
> = {
  edu: { fr: "ITaaS Edu", en: "ITaaS Edu" },
  rh: { fr: "ITaaS RH", en: "ITaaS HR" },
  mineportal: { fr: "ITaaS MinePortal", en: "ITaaS MinePortal" },
  inventory: { fr: "ITaaS Inventory", en: "ITaaS Inventory" },
  erp: { fr: "ITaaS ERP", en: "ITaaS ERP" },
  billing: { fr: "ITaaS Invoice", en: "ITaaS Invoice" },
  hospitol: { fr: "ITaaS Hospitol", en: "ITaaS Hospitol" },
  security: { fr: "ITaaS Security", en: "ITaaS Security" },
};

export const PRODUCTS_PUBLISHABLE_SLUGS = new Set<string>(
  Object.keys(PRODUCTS_COMMERCIAL_NAMES),
);

/**
 * Commercial product copy (distinct from Solutions journeys).
 * Promise = buyer outcome · Capacities = concrete product capabilities.
 * Truth-bound: no absolute MINEDU, no end-to-end logistics overclaim, no Comex/CRM,
 * no statutory payroll conformity claim, ERP = progressive foundation, LIMITED = controlled offer.
 */
export const PRODUCTS_PRIMARY: readonly ProductsEditorial[] = [
  {
    slug: "edu",
    tier: "primary",
    order: 1,
    commercialNameFr: "ITaaS Edu",
    commercialNameEn: "ITaaS Edu",
    imageSrc: "/assets/home/home-solution-edu.webp",
    imageAltFr: "Illustration éditoriale éducation — pas une capture produit.",
    imageAltEn: "Education editorial illustration — not a product screenshot.",
    promiseFr:
      "Le logiciel scolaire pour piloter inscriptions, classes, évaluations et relation parents.",
    promiseEn:
      "School management software to run enrollment, classes, assessments and parent engagement.",
    capacitiesFr: [
      "Inscriptions, classes et scolarité au quotidien",
      "Évaluations, notes et bulletins",
      "Suivi des absences et de la présence",
      "Espace parents et communication école–famille",
    ],
    capacitiesEn: [
      "Day-to-day enrollment, classes and schooling",
      "Assessments, grades and report cards",
      "Absence and attendance tracking",
      "Parent space and school–family communication",
    ],
    tone: "edu",
  },
  {
    slug: "rh",
    tier: "primary",
    order: 2,
    commercialNameFr: "ITaaS RH",
    commercialNameEn: "ITaaS HR",
    imageSrc: "/assets/home/home-solution-rh.webp",
    imageAltFr: "Illustration éditoriale RH — pas une capture produit.",
    imageAltEn: "HR editorial illustration — not a product screenshot.",
    promiseFr:
      "Un produit RH pour centraliser dossiers, administration et recrutement.",
    promiseEn:
      "An HR product to centralize employee records, administration and recruiting.",
    capacitiesFr: [
      "Dossiers collaborateurs structurés",
      "Administration RH du quotidien",
      "Portail carrières pour publier et recevoir des candidatures",
      "Demandes et suivi des congés",
    ],
    capacitiesEn: [
      "Structured employee records",
      "Day-to-day HR administration",
      "Careers portal to post roles and receive applications",
      "Leave requests and follow-up",
    ],
    tone: "rh",
  },
  {
    slug: "mineportal",
    tier: "primary",
    order: 3,
    commercialNameFr: "ITaaS MinePortal",
    commercialNameEn: "ITaaS MinePortal",
    imageSrc: "/assets/home/home-solution-mineportal.webp",
    imageAltFr: "Illustration éditoriale mines — pas une capture produit.",
    imageAltEn: "Mining editorial illustration — not a product screenshot.",
    promiseFr:
      "Le portail opérationnel pour gouverner sites miniers, sous-traitance et magasins.",
    promiseEn:
      "The operations portal to govern mining sites, subcontracting and stores.",
    capacitiesFr: [
      "Vue consolidée des sites et opérations",
      "Sous-traitance et flux ARSP",
      "Magasins et traçabilité des matières",
      "Circuits de validation terrain",
    ],
    capacitiesEn: [
      "Consolidated site and operations view",
      "Subcontracting and ARSP flows",
      "Stores and material traceability",
      "Field validation workflows",
    ],
    tone: "mine",
  },
  {
    slug: "inventory",
    tier: "primary",
    order: 4,
    commercialNameFr: "ITaaS Inventory",
    commercialNameEn: "ITaaS Inventory",
    imageSrc: "/assets/home/home-solution-inventory.webp",
    imageAltFr: "Illustration éditoriale stocks — pas une capture produit.",
    imageAltEn: "Inventory editorial illustration — not a product screenshot.",
    promiseFr:
      "Le produit stocks pour maîtriser catalogue, entrepôts et mouvements.",
    promiseEn:
      "Inventory software to control catalog, warehouses and stock movements.",
    capacitiesFr: [
      "Catalogue articles structuré",
      "Entrepôts et niveaux de stock",
      "Mouvements d’entrée et de sortie",
      "Traçabilité des flux stockés",
    ],
    capacitiesEn: [
      "Structured item catalog",
      "Warehouses and stock levels",
      "Inbound and outbound movements",
      "Traceability of stored flows",
    ],
    tone: "inventory",
  },
  {
    slug: "erp",
    tier: "primary",
    order: 5,
    commercialNameFr: "ITaaS ERP",
    commercialNameEn: "ITaaS ERP",
    imageSrc: "/assets/home/home-solution-erp.webp",
    imageAltFr: "Illustration éditoriale ERP — pas une capture produit.",
    imageAltEn: "ERP editorial illustration — not a product screenshot.",
    promiseFr:
      "Le socle ERP pour structurer organisation, sites et rôles — prêt à s’étendre.",
    promiseEn:
      "The ERP foundation to structure organization, sites and roles — ready to extend.",
    capacitiesFr: [
      "Organisation, départements et rôles",
      "Pilotage multi-sites",
      "Collaboration opérationnelle",
      "Fondation progressive pour la suite ITaaS",
    ],
    capacitiesEn: [
      "Organization, departments and roles",
      "Multi-site steering",
      "Operational collaboration",
      "Progressive foundation for the ITaaS suite",
    ],
    tone: "erp",
  },
  {
    slug: "billing",
    tier: "primary",
    order: 6,
    commercialNameFr: "ITaaS Invoice",
    commercialNameEn: "ITaaS Invoice",
    imageSrc: "/assets/home/home-solution-billing.webp",
    imageAltFr: "Illustration éditoriale facturation — pas une capture produit.",
    imageAltEn: "Billing editorial illustration — not a product screenshot.",
    promiseFr:
      "Le produit facturation pour enchaîner devis, factures et encaissements.",
    promiseEn:
      "Billing software to run quotes, invoices and collections in one flow.",
    capacitiesFr: [
      "Devis et factures professionnels",
      "Suivi des paiements clients",
      "Fichier clients centralisé",
      "Rappels et relances de règlement",
    ],
    capacitiesEn: [
      "Professional quotes and invoices",
      "Customer payment follow-up",
      "Centralized customer file",
      "Payment reminders and follow-ups",
    ],
    tone: "billing",
  },
] as const;

export const PRODUCTS_LIMITED: readonly ProductsEditorial[] = [
  {
    slug: "hospitol",
    tier: "limited",
    order: 7,
    commercialNameFr: "ITaaS Hospitol",
    commercialNameEn: "ITaaS Hospitol",
    imageSrc: "/assets/home/home-solution-hospitol.webp",
    imageAltFr: "Illustration éditoriale santé — pas une capture produit.",
    imageAltEn: "Healthcare editorial illustration — not a product screenshot.",
    promiseFr:
      "Pilotez pharmacie hospitalière et stock médical — offre en disponibilité limitée.",
    promiseEn:
      "Run hospital pharmacy and medical stock — limited-availability offer.",
    capacitiesFr: [
      "Pharmacie hospitalière opérationnelle",
      "Stock médical et dispensation",
      "Lien avec la facturation associée",
    ],
    capacitiesEn: [
      "Operational hospital pharmacy",
      "Medical stock and dispensing",
      "Link to associated billing",
    ],
    tone: "hospitol",
  },
  {
    slug: "security",
    tier: "limited",
    order: 8,
    commercialNameFr: "ITaaS Security",
    commercialNameEn: "ITaaS Security",
    imageSrc: "/assets/home/home-solution-security.webp",
    imageAltFr: "Illustration éditoriale sécurité — pas une capture produit.",
    imageAltEn: "Security editorial illustration — not a product screenshot.",
    promiseFr:
      "Organisez clients, agents et incidents de sécurité privée — offre contrôlée.",
    promiseEn:
      "Organize private-security clients, agents and incidents — controlled offer.",
    capacitiesFr: [
      "Portefeuille clients et sites protégés",
      "Agents, affectations et certifications",
      "Enregistrement et suivi des incidents",
    ],
    capacitiesEn: [
      "Client portfolio and protected sites",
      "Agents, assignments and certifications",
      "Incident logging and follow-up",
    ],
    tone: "security",
  },
] as const;

export const PRODUCTS_HERO_PANELS: readonly ProductsHeroPanel[] = [
  {
    id: "education",
    imageSrc: "/assets/home/home-solution-edu.jpg",
    labelFr: "Éducation",
    labelEn: "Education",
    altFr: "Éducation — illustration éditoriale.",
    altEn: "Education — editorial illustration.",
  },
  {
    id: "hr",
    imageSrc: "/assets/home/home-solution-rh.jpg",
    labelFr: "Ressources humaines",
    labelEn: "Human resources",
    altFr: "Ressources humaines — illustration éditoriale.",
    altEn: "Human resources — editorial illustration.",
  },
  {
    id: "finance",
    imageSrc: "/assets/home/home-solution-billing.jpg",
    labelFr: "Finance & gestion",
    labelEn: "Finance & management",
    altFr: "Finance et gestion — illustration éditoriale.",
    altEn: "Finance and management — editorial illustration.",
  },
  {
    id: "ops",
    imageSrc: "/assets/home/home-solution-inventory.jpg",
    labelFr: "Opérations & stocks",
    labelEn: "Operations & inventory",
    altFr: "Opérations et stocks — illustration éditoriale.",
    altEn: "Operations and inventory — editorial illustration.",
  },
  {
    id: "mines",
    imageSrc: "/assets/home/home-solution-mineportal.jpg",
    labelFr: "Industrie & mines",
    labelEn: "Industry & mining",
    altFr: "Industrie et mines — illustration éditoriale.",
    altEn: "Industry and mining — editorial illustration.",
  },
] as const;

export function commercialProductName(slug: string, locale: PepLocale): string | undefined {
  const entry = PRODUCTS_COMMERCIAL_NAMES[slug as ProductsSlug];
  if (!entry) return undefined;
  return locale === "en" ? entry.en : entry.fr;
}

export function productsCopy(locale: PepLocale) {
  const en = locale === "en";
  return {
    seoTitle: en
      ? "Business software for organizations in the DRC | ITaaS Suite"
      : "Logiciels métiers pour entreprises en RDC | ITaaS Suite",
    seoDescription: en
      ? "Explore the ITaaS Suite product portfolio — Edu, HR, MinePortal, Inventory, ERP, Invoice, plus limited Hospitol and Security. Built for organizations in the DRC and Africa."
      : "Explorez le portefeuille produits ITaaS Suite — Edu, RH, MinePortal, Inventory, ERP, Invoice, plus Hospitol et Security en disponibilité limitée. Conçu pour les organisations en RDC et en Afrique.",
    seoKeywords: en
      ? ["business software DRC", "ITaaS Suite products", "school HR inventory mining Congo"]
      : ["logiciels métiers RDC", "catalogue ITaaS Suite", "éducation RH stocks mines Congo"],
    eyebrow: en ? "ITAAS SUITE CATALOG" : "CATALOGUE ITAAS SUITE",
    h1Before: en ? "A complete suite of " : "Une suite complète de ",
    h1Emphasis: en ? "business software" : "logiciels métiers",
    h1After: en ? " for your organization." : " pour votre organisation.",
    lead: en
      ? "ITaaS Suite brings together integrated, scalable products to manage, steer and grow your organization in the DRC and across Africa."
      : "ITaaS Suite rassemble des produits intégrés et évolutifs pour gérer, piloter et faire grandir votre organisation en RDC et en Afrique.",
    searchPlaceholder: en ? "Search for a product or capability…" : "Rechercher un produit, une fonction…",
    searchAria: en ? "Search the product catalog" : "Rechercher dans le catalogue produits",
    compareCta: en ? "Compare products" : "Comparer les produits",
    compareHref: "/compare",
    solutionsCta: en ? "Browse our solutions" : "Voir nos solutions",
    solutionsHref: "/solutions",
    trust: en
      ? ([
          { id: "rdc", title: "Built in the DRC", body: "By our experts in Lubumbashi" },
          { id: "secure", title: "Secured", body: "Protected and confidential data" },
          { id: "support", title: "Guided rollout", body: "Deployment, support and training" },
        ] as const)
      : ([
          { id: "rdc", title: "Conçu en RDC", body: "Par nos experts à Lubumbashi" },
          { id: "secure", title: "Sécurisé", body: "Données protégées et confidentielles" },
          { id: "support", title: "Accompagnement", body: "Déploiement, support et formation" },
        ] as const),
    portfolioEyebrow: en ? "OUR PORTFOLIO" : "NOTRE PORTEFEUILLE",
    portfolioTitle: en ? "Our 6 primary products" : "Nos 6 produits principaux",
    portfolioLead: en
      ? "Complete, proven and evolving solutions."
      : "Des solutions complètes, éprouvées et évolutives.",
    portfolioAll: en ? "See all products" : "Voir tous les produits",
    discover: en ? "Discover the product" : "Découvrir le produit",
    limitedEyebrow: en ? "LIMITED AVAILABILITY OFFERS" : "OFFRES EN DISPONIBILITÉ LIMITÉE",
    limitedTitle: en ? "Limited availability" : "Disponibilité limitée",
    limitedLead: en
      ? "Two additional products — Hospitol and Security — offered within a controlled commercial scope."
      : "Deux produits complémentaires — Hospitol et Security — proposés dans un périmètre commercial contrôlé.",
    limitedBadge: en ? "LIMITED" : "LIMITÉ",
    limitedContactTitle: en ? "Want access?" : "Vous souhaitez y accéder ?",
    limitedContactBody: en
      ? "Contact our team for more information."
      : "Contactez notre équipe pour plus d’informations.",
    limitedContactCta: en ? "Contact us" : "Nous contacter",
    platformEyebrow: en ? "AN INTEGRATED ECOSYSTEM" : "UN ÉCOSYSTÈME INTÉGRÉ",
    platformTitle: en
      ? "Products designed to work together"
      : "Des produits conçus pour travailler ensemble",
    platformLead: en
      ? "Every product shares the ITaaS Platform foundation — identity, data, workflows, reporting and open integrations."
      : "Chaque produit partage la fondation ITaaS Platform — identité, données, workflows, reporting et intégrations ouvertes.",
    platformCta: en ? "See why ITaaS" : "Voir pourquoi ITaaS",
    platformHref: "/why-itaas",
    platformCenter: "ITaaS PLATFORM",
    platformNodes: en
      ? ([
          "Identity & access",
          "Centralized data",
          "Workflows & automation",
          "Reporting & BI",
          "Open integrations",
        ] as const)
      : ([
          "Identités & accès",
          "Données centralisées",
          "Workflows & automatisation",
          "Reporting & BI",
          "Intégrations ouvertes",
        ] as const),
    finalTitle: en
      ? "Find the right product to transform your organization."
      : "Trouvez le bon produit pour transformer votre organisation.",
    finalLead: en
      ? "Compose your environment in Configure, or talk to our team for a guided demo."
      : "Composez votre environnement dans Configure, ou échangez avec notre équipe pour une démo guidée.",
    finalConfigure: en ? "Create my environment" : "Créer mon environnement",
    finalConfigureHref: "/configure",
    finalDemo: en ? "Request a demonstration" : "Demander une démonstration",
    finalContact: en ? "Contact us" : "Nous contacter",
    loading: en ? "Loading…" : "Chargement…",
    emptySearch: en ? "No product matches your search." : "Aucun produit ne correspond à votre recherche.",
    apiErrorTitle: en ? "Catalog temporarily unavailable" : "Catalogue temporairement indisponible",
    apiErrorBody: en
      ? "We could not load the product catalog. Please try again in a moment or contact ITaaS RDC."
      : "Nous n’avons pas pu charger le catalogue produits. Réessayez dans un instant ou contactez ITaaS RDC.",
    retry: en ? "Try again" : "Réessayer",
  };
}
